/** Nearest-rank percentile of `xs` (0 for an empty set). */
const percentile = (xs: readonly number[], p: number): number => {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
};

/**
 * Pacing knobs. These are baked into {@link DEFAULT_REVEAL_PACER_TUNING} — production
 * never sets them; the type + override arg exist only so a tuning sweep can explore
 * alternatives.
 */
export type RevealPacerTuning = {
  /** Reveal-timer period (ms); the host's reveal loop reads {@link AdaptiveRevealPacer.tickMs} so its cadence matches the `rate · tickMs` math. */
  tickMs: number;
  /** Floor on chars revealed per tick. */
  minChars: number;
  /** Pre-stream all-at-once reveal: fraction of the still-buffered tail revealed per tick (eased decay — fast, then easing into the runway). */
  revealFraction: number;
  /** Initial arrival-rate estimate (chars/ms) before the first gap is observed. */
  seedChPerMs: number;
  /** EMA smoothing for the arrival-rate estimate (0..1; higher = more reactive). */
  emaAlpha: number;
  /** How many recent inter-arrival gaps to keep for the jitter estimate. */
  gapWindow: number;
  /** Percentile of recent gaps the cushion covers (e.g. 85). */
  gapPercentile: number;
  /** Fraction of the buffer-above-cushion drained per tick (two-sided catch-up). */
  catchup: number;
  /**
   * Cold-start cushion (ms), used only until the first real gap is measured. Without it
   * the opening chunk has a zero cushion, drains greedily, and stalls until the next
   * chunk — the lone residual stall. Capped by {@link maxLatencyMs}.
   */
  seedGapMs: number;
  /** Cap (ms) on the held cushion — the smoothness↔latency knob. */
  maxLatencyMs: number;
};

/**
 * Tuned against real model-emit cadences (Sonnet ~66-char bursts every ~400ms) + the
 * 50ms server debounce. `maxLatencyMs` is the one UX dial
 * (smoothness vs. latency); the cushion self-tunes below it from observed jitter, so a
 * smooth fast stream holds ~nothing and chunky Sonnet holds up to the cap. `seedGapMs`
 * seeds that cushion for the first chunk only (before any gap is measured) so the opening
 * doesn't drain-then-stall. `minChars` must stay 1: a floor of 2 (~125 char/s) outpaces
 * slow models and reintroduces stalls.
 */
export const DEFAULT_REVEAL_PACER_TUNING: RevealPacerTuning = {
  tickMs: 16,
  minChars: 1,
  revealFraction: 0.16,
  seedChPerMs: 0.17,
  emaAlpha: 0.3,
  gapWindow: 8,
  gapPercentile: 85,
  catchup: 0.08,
  seedGapMs: 300,
  maxLatencyMs: 300,
};

/**
 * The reveal-pacing authority: given the unrevealed-buffer depth, it answers "how many
 * chars should I reveal this tick?" and owns the tick period ({@link tickMs}) the host
 * loop runs at. The host owns only the loop itself, the buffers, and atomic-range
 * snapping — all cadence decisions live here.
 *
 * Two modes, selected automatically:
 *  - Before any server-driven delta ({@link isStreaming} false) — the all-at-once
 *    cosmetic case where the whole string is already known — it eases the buffer out
 *    with a simple fraction decay (fast, then easing into the runway).
 *  - Once deltas arrive, it folds each one's size + time into an arrival-rate EMA and
 *    inter-arrival jitter estimate, then paces the reveal at the arrival rate while
 *    holding a self-sized cushion across the model's gaps — instead of draining each
 *    burst to empty and stalling ("catch up, stop, lurch").
 *
 * Pure pacing arithmetic: it knows nothing about markdown, the DOM, or how characters
 * are sliced. Stateful — one instance per stream. Injecting `now` into
 * {@link recordArrival} keeps it unit-testable without a fake clock.
 */
export class AdaptiveRevealPacer {
  /** EMA of the measured arrival rate (chars/ms); seeded until the first gap is observed. */
  private emaRate: number;
  /** Arrival time (`performance.now()`, or injected) of the last recorded delta. */
  private lastArrivalAt: number | undefined;
  /** Cumulative content length at the last recorded delta, for the per-delta rate. */
  private lastArrivalLen = 0;
  /** Windowed recent inter-arrival gaps (ms) — the jitter estimate that sizes the cushion. */
  private readonly gaps: number[] = [];
  /** Latches true on the first recorded delta; gates {@link isStreaming}. */
  private streaming = false;

  /** @param tuning pacing knobs; the default is tuned for real cadence — override only in the sim sweep. */
  constructor(private readonly tuning: RevealPacerTuning = DEFAULT_REVEAL_PACER_TUNING) {
    this.emaRate = tuning.seedChPerMs;
  }

  /** Reveal-timer period (ms) — the host's reveal loop runs at this interval so its cadence matches `rate · tickMs`. */
  get tickMs(): number {
    return this.tuning.tickMs;
  }

  /** True once a server-driven delta has been recorded (selects the adaptive reveal over the fraction decay). */
  get isStreaming(): boolean {
    return this.streaming;
  }

  /** Fold one server-driven delta (its new cumulative length + arrival time) into the estimate. */
  recordArrival(newLen: number, now: number = performance.now()): void {
    this.streaming = true;
    if (
      this.lastArrivalAt !== undefined &&
      now > this.lastArrivalAt &&
      newLen > this.lastArrivalLen
    ) {
      const gap = now - this.lastArrivalAt;
      const instRate = (newLen - this.lastArrivalLen) / gap; // chars/ms
      this.emaRate = this.tuning.emaAlpha * instRate + (1 - this.tuning.emaAlpha) * this.emaRate;
      this.gaps.push(gap);
      if (this.gaps.length > this.tuning.gapWindow) this.gaps.shift();
    }
    this.lastArrivalAt = now;
    this.lastArrivalLen = newLen;
  }

  /**
   * Chars to reveal this tick for `buffer` unrevealed chars.
   *
   * Pre-stream (all-at-once): a simple fraction-of-buffer decay. Once streaming: holds
   * a cushion sized to the observed gap jitter (or {@link RevealPacerTuning.seedGapMs}
   * for the first chunk, before any gap is measured) and reveals at the arrival rate plus
   * a two-sided correction toward that cushion (build below, drain above); `complete`
   * drops the cushion to 0 so the tail accelerates out once the authoritative value is known.
   */
  charsThisTick(buffer: number, complete: boolean): number {
    if (!this.streaming) {
      return Math.max(this.tuning.minChars, Math.ceil(buffer * this.tuning.revealFraction));
    }
    // Until the first gap is measured, fall back to the seed cushion so the opening
    // chunk holds back instead of draining greedily and stalling; from gap #1 on, use
    // the jitter-derived value (so a fast stream snaps to its real, near-zero cushion).
    const cushionMs =
      this.gaps.length === 0
        ? this.tuning.seedGapMs
        : percentile(this.gaps, this.tuning.gapPercentile);
    const targetLatencyMs = complete ? 0 : Math.min(this.tuning.maxLatencyMs, cushionMs);
    const desired = this.emaRate * targetLatencyMs;
    const correction = (buffer - desired) * this.tuning.catchup;
    return Math.max(
      this.tuning.minChars,
      Math.ceil(this.emaRate * this.tuning.tickMs + correction),
    );
  }
}
